import { IconDefinition } from '@fortawesome/fontawesome-svg-core';
import {
  faUsers,
  faMobileAlt,
  faHeadset,
  faChartLine,
  faDollarSign,
  faCreditCard,
  faChartBar,
  faCheckCircle,
} from '@fortawesome/free-solid-svg-icons';
import jsPDF from 'jspdf';
import type { ExportData } from './types';

export const METRIC_CARDS = {
  TOTAL_MERCHANTS: 'totalMerchants',
  ACTIVE_DEVICES: 'activeDevices',
  SUPPORT_TICKETS: 'supportTickets',
  TOTAL_REVENUE: 'totalRevenue',
  TOTAL_ORDERS: 'totalOrders',
  SYSTEM_HEALTH: 'systemHealth',
  CONVERSION_RATE: 'conversionRate',
  AVG_ORDER_VALUE: 'avgOrderValue',
  CUSTOMER_SATISFACTION: 'customerSatisfaction',
  RECENT_TRANSACTIONS: 'recentTransactions',
} as const;

export type MetricCardType = typeof METRIC_CARDS[keyof typeof METRIC_CARDS];

export interface MetricCardConfig {
  id: MetricCardType;
  title: string;
  subtitle?: string;
  icon: IconDefinition;
  color: string;
  trend?: 'up' | 'down';
  trendValue?: string;
  onClick?: boolean;
}

export const METRIC_CARDS_CONFIG: MetricCardConfig[] = [
  {
    id: METRIC_CARDS.TOTAL_MERCHANTS,
    title: 'Total Merchants',
    subtitle: 'Active business partners',
    icon: faUsers,
    color: '#3b82f6',
    trend: 'up',
    trendValue: '+12% this month',
    onClick: true,
  },
  {
    id: METRIC_CARDS.ACTIVE_DEVICES,
    title: 'Active Devices',
    subtitle: 'Connected kiosks',
    icon: faMobileAlt,
    color: '#10b981',
    trend: 'up',
    trendValue: '+8% this week',
    onClick: true,
  },
  {
    id: METRIC_CARDS.SUPPORT_TICKETS,
    title: 'Support Tickets',
    subtitle: 'Pending resolution',
    icon: faHeadset,
    color: '#f59e0b',
    trend: 'down',
    trendValue: '-3% this week',
    onClick: true,
  },
  {
    id: METRIC_CARDS.TOTAL_REVENUE,
    title: 'Total Revenue',
    subtitle: 'Platform earnings',
    icon: faDollarSign,
    color: '#10b981',
    trend: 'up',
    trendValue: '+15% this month',
  },
  {
    id: METRIC_CARDS.TOTAL_ORDERS,
    title: 'Total Orders',
    subtitle: 'Completed transactions',
    icon: faChartBar,
    color: '#3b82f6',
    trend: 'up',
    trendValue: '+20% this month',
  },
  {
    id: METRIC_CARDS.SYSTEM_HEALTH,
    title: 'System Health',
    subtitle: 'Platform uptime',
    icon: faCheckCircle,
    color: '#10b981',
    trend: 'up',
    trendValue: 'Stable',
  },
  {
    id: METRIC_CARDS.CONVERSION_RATE,
    title: 'Conversion Rate',
    subtitle: 'Customer conversion',
    icon: faChartLine,
    color: '#3b82f6',
    trend: 'up',
    trendValue: '+2.5% this week',
  },
  {
    id: METRIC_CARDS.AVG_ORDER_VALUE,
    title: 'Avg Order Value',
    subtitle: 'Per transaction',
    icon: faDollarSign,
    color: '#10b981',
    trend: 'up',
    trendValue: '+$3.20 this month',
  },
  {
    id: METRIC_CARDS.CUSTOMER_SATISFACTION,
    title: 'Customer Satisfaction',
    subtitle: 'Rating score',
    icon: faUsers,
    color: '#f59e0b',
    trend: 'up',
    trendValue: '+0.3 this month',
  },
  {
    id: METRIC_CARDS.RECENT_TRANSACTIONS,
    title: 'Recent Transactions',
    subtitle: 'Latest activity',
    icon: faCreditCard,
    color: '#3b82f6',
    trend: 'up',
    trendValue: '+15 this week',
  },
];

export const API_ENDPOINTS = {
  MERCHANTS: 'merchant',
  DEVICES: 'devices',
  ORDERS: 'orders',
  REVENUE: 'analytics/admin/revenue',
  TRANSACTIONS: 'analytics/admin/transactions',
  PERFORMANCE: 'analytics/admin/performance',
  SUPPORT: 'analytics/admin/support',
} as const;

export const REFRESH_INTERVAL = 5 * 60 * 1000; // 5 minutes

export const EXPORT_TYPES = {
  MERCHANTS_OVERVIEW: 'Merchants Overview',
  ACTIVE_DEVICES: 'Active Devices',
  COMPREHENSIVE_REPORT: 'Comprehensive Admin Report',
} as const;

export const TABLE_COLUMNS = {
  MERCHANTS: ['Merchant Name', 'Address', 'Email', 'Phone', 'Status'],
  DEVICES: ['Device Name', 'Identifier', 'Type', 'Status'],
} as const;

export const EMPTY_MESSAGES = {
  MERCHANTS: 'No merchants found',
  DEVICES: 'No devices found',
  TRANSACTIONS: 'No transactions available',
} as const;

export const COLORS = {
  PRIMARY: '#3b82f6',
  SUCCESS: '#10b981',
  WARNING: '#f59e0b',
  DANGER: '#ef4444',
} as const;

// PDF Generation Configuration
export const PDF_CONFIG = {
  DOCUMENT: {
    SUBJECT: 'Kiosk Admin Platform Report',
    AUTHOR: 'Kiosk Admin System',
    CREATOR: 'Kiosk Admin Platform',
  },
  FONT_SIZES: {
    HEADER: 24,
    TITLE: 18,
    SUBTITLE: 16,
    BODY: 12,
    SMALL: 10,
    FOOTER: 10,
  },
  POSITIONS: {
    START_X: 20,
    HEADER_Y: 30,
    TITLE_Y: 45,
    DATE_Y: 60,
    CONTENT_START_Y: 80,
    INDENT_X: 30,
    DETAIL_INDENT_X: 40,
    FOOTER_Y_OFFSET: 20,
    FOOTER_TEXT_Y_OFFSET: 10,
    PAGE_BREAK_THRESHOLD: 250,
    NEW_PAGE_START_Y: 20,
  },
  SPACING: {
    SECTION_SPACING: 20,
    LINE_SPACING: 12,
    ITEM_SPACING: 40,
    DETAIL_ITEM_SPACING: 35,
    TRANSACTION_SPACING: 15,
  },
  COLORS: {
    FOOTER_TEXT: [128, 128, 128],
  },
  FOOTER: {
    TEXT: 'Generated by Kiosk Admin Platform',
    CONFIDENTIAL: 'Confidential - For internal use only',
  },
  LIMITS: {
    MAX_TRANSACTIONS: 20,
  },
} as const;

// PDF Generation Function
export const generatePDF = (type: string, data: ExportData): jsPDF => {
  try {
    if (!type || !data) {
      throw new Error('Invalid type or data provided for PDF generation');
    }

    const doc = new jsPDF();

    doc.setProperties({
      title: `${type} Report`,
      subject: PDF_CONFIG.DOCUMENT.SUBJECT,
      author: PDF_CONFIG.DOCUMENT.AUTHOR,
      creator: PDF_CONFIG.DOCUMENT.CREATOR,
    });

    // Add header
    doc.setFontSize(PDF_CONFIG.FONT_SIZES.HEADER);
    doc.setFont('helvetica', 'bold');
    doc.text('Kiosk Admin Platform', PDF_CONFIG.POSITIONS.START_X, PDF_CONFIG.POSITIONS.HEADER_Y);

    doc.setFontSize(PDF_CONFIG.FONT_SIZES.TITLE);
    doc.text(`${type} Report`, PDF_CONFIG.POSITIONS.START_X, PDF_CONFIG.POSITIONS.TITLE_Y);

    doc.setFontSize(PDF_CONFIG.FONT_SIZES.BODY);
    doc.setFont('helvetica', 'normal');
    doc.text(`Generated: ${new Date().toLocaleString()}`, PDF_CONFIG.POSITIONS.START_X, PDF_CONFIG.POSITIONS.DATE_Y);

    let yPosition: number = PDF_CONFIG.POSITIONS.CONTENT_START_Y;

    // Add content based on type
    if (type === EXPORT_TYPES.MERCHANTS_OVERVIEW) {
      doc.setFontSize(PDF_CONFIG.FONT_SIZES.SUBTITLE);
      doc.setFont('helvetica', 'bold');
      doc.text(`Total Merchants: ${data.totalCount || 0}`, PDF_CONFIG.POSITIONS.START_X, yPosition);
      yPosition += PDF_CONFIG.SPACING.SECTION_SPACING;

      doc.setFontSize(PDF_CONFIG.FONT_SIZES.BODY);
      doc.setFont('helvetica', 'normal');

      (data.data || []).forEach((merchant: any, index: number) => {
        if (yPosition > PDF_CONFIG.POSITIONS.PAGE_BREAK_THRESHOLD) {
          doc.addPage();
          yPosition = PDF_CONFIG.POSITIONS.NEW_PAGE_START_Y;
        }

        doc.setFont('helvetica', 'bold');
        doc.text(`${index + 1}. ${merchant.name}`, PDF_CONFIG.POSITIONS.INDENT_X, yPosition);
        doc.setFont('helvetica', 'normal');
        doc.text(`Email: ${merchant.email}`, PDF_CONFIG.POSITIONS.DETAIL_INDENT_X, yPosition + 8);
        doc.text(`Phone: ${merchant.phone}`, PDF_CONFIG.POSITIONS.DETAIL_INDENT_X, yPosition + 16);
        doc.text(`Status: ${merchant.status ? 'Active' : 'Inactive'}`, PDF_CONFIG.POSITIONS.DETAIL_INDENT_X, yPosition + 24);
        yPosition += PDF_CONFIG.SPACING.ITEM_SPACING;
      });
    } else if (type === EXPORT_TYPES.ACTIVE_DEVICES) {
      doc.setFontSize(PDF_CONFIG.FONT_SIZES.SUBTITLE);
      doc.setFont('helvetica', 'bold');
      doc.text(`Total Devices: ${data.totalCount || 0}`, PDF_CONFIG.POSITIONS.START_X, yPosition);
      yPosition += PDF_CONFIG.SPACING.SECTION_SPACING;

      doc.setFontSize(PDF_CONFIG.FONT_SIZES.BODY);
      doc.setFont('helvetica', 'normal');

      (data.data || []).forEach((device: any, index: number) => {
        if (yPosition > PDF_CONFIG.POSITIONS.PAGE_BREAK_THRESHOLD) {
          doc.addPage();
          yPosition = PDF_CONFIG.POSITIONS.NEW_PAGE_START_Y;
        }

        doc.setFont('helvetica', 'bold');
        doc.text(`${index + 1}. ${device.name}`, PDF_CONFIG.POSITIONS.INDENT_X, yPosition);
        doc.setFont('helvetica', 'normal');
        doc.text(`ID: ${device.identifier}`, PDF_CONFIG.POSITIONS.DETAIL_INDENT_X, yPosition + 8);
        doc.text(`Type: ${device.type}`, PDF_CONFIG.POSITIONS.DETAIL_INDENT_X, yPosition + 16);
        doc.text(`Status: ${device.status ? 'Active' : 'Inactive'}`, PDF_CONFIG.POSITIONS.DETAIL_INDENT_X, yPosition + 24);
        yPosition += PDF_CONFIG.SPACING.ITEM_SPACING;
      });
    } else if (type === EXPORT_TYPES.COMPREHENSIVE_REPORT) {
      // Executive Summary
      doc.setFontSize(PDF_CONFIG.FONT_SIZES.SUBTITLE);
      doc.setFont('helvetica', 'bold');
      doc.text('Executive Summary', PDF_CONFIG.POSITIONS.START_X, yPosition);
      yPosition += PDF_CONFIG.SPACING.SECTION_SPACING;

      doc.setFontSize(PDF_CONFIG.FONT_SIZES.BODY);
      doc.setFont('helvetica', 'normal');
      if (data.summary) {
        doc.text(`Total Merchants: ${data.summary.totalMerchants}`, PDF_CONFIG.POSITIONS.INDENT_X, yPosition);
        yPosition += PDF_CONFIG.SPACING.LINE_SPACING;
        doc.text(`Active Devices: ${data.summary.activeDevices}`, PDF_CONFIG.POSITIONS.INDENT_X, yPosition);
        yPosition += PDF_CONFIG.SPACING.LINE_SPACING;
        doc.text(`Support Tickets: ${data.summary.supportTickets}`, PDF_CONFIG.POSITIONS.INDENT_X, yPosition);
        yPosition += PDF_CONFIG.SPACING.LINE_SPACING;
        doc.text(
          `Total Revenue: $${data.summary.totalRevenue.toLocaleString()}`,
          PDF_CONFIG.POSITIONS.INDENT_X,
          yPosition
        );
        yPosition += PDF_CONFIG.SPACING.LINE_SPACING;
        doc.text(`Total Orders: ${data.summary.totalOrders}`, PDF_CONFIG.POSITIONS.INDENT_X, yPosition);
        yPosition += PDF_CONFIG.SPACING.SECTION_SPACING;

        // Performance Metrics
        doc.setFontSize(PDF_CONFIG.FONT_SIZES.SUBTITLE);
        doc.setFont('helvetica', 'bold');
        doc.text('Performance Metrics', PDF_CONFIG.POSITIONS.START_X, yPosition);
        yPosition += PDF_CONFIG.SPACING.SECTION_SPACING;

        doc.setFontSize(PDF_CONFIG.FONT_SIZES.BODY);
        doc.setFont('helvetica', 'normal');
        const metrics = data.summary.performanceMetrics;
        doc.text(
          `Conversion Rate: ${(metrics.conversionRate * 100).toFixed(1)}%`,
          PDF_CONFIG.POSITIONS.INDENT_X,
          yPosition
        );
        yPosition += PDF_CONFIG.SPACING.LINE_SPACING;
        doc.text(
          `Average Order Value: $${metrics.averageOrderValue.toFixed(2)}`,
          PDF_CONFIG.POSITIONS.INDENT_X,
          yPosition
        );
        yPosition += PDF_CONFIG.SPACING.LINE_SPACING;
        doc.text(
          `Customer Satisfaction: ${metrics.customerSatisfaction}/5.0`,
          PDF_CONFIG.POSITIONS.INDENT_X,
          yPosition
        );
        yPosition += PDF_CONFIG.SPACING.LINE_SPACING;
        doc.text(`System Uptime: ${metrics.systemUptime}%`, PDF_CONFIG.POSITIONS.INDENT_X, yPosition);
        yPosition += PDF_CONFIG.SPACING.SECTION_SPACING;
      }

      // Add detailed merchant data
      if (data.detailedData?.merchants && data.detailedData.merchants.length > 0) {
        doc.addPage();
        yPosition = PDF_CONFIG.POSITIONS.NEW_PAGE_START_Y;

        doc.setFontSize(PDF_CONFIG.FONT_SIZES.SUBTITLE);
        doc.setFont('helvetica', 'bold');
        doc.text('Merchants Details', PDF_CONFIG.POSITIONS.START_X, yPosition);
        yPosition += PDF_CONFIG.SPACING.SECTION_SPACING;

        doc.setFontSize(PDF_CONFIG.FONT_SIZES.SMALL);
        doc.setFont('helvetica', 'normal');

        data.detailedData.merchants.forEach((merchant: any, index: number) => {
          if (yPosition > PDF_CONFIG.POSITIONS.PAGE_BREAK_THRESHOLD) {
            doc.addPage();
            yPosition = PDF_CONFIG.POSITIONS.NEW_PAGE_START_Y;
          }

          doc.setFont('helvetica', 'bold');
          doc.text(`${index + 1}. ${merchant.name}`, PDF_CONFIG.POSITIONS.START_X, yPosition);
          doc.setFont('helvetica', 'normal');
          doc.text(`Email: ${merchant.email}`, PDF_CONFIG.POSITIONS.INDENT_X, yPosition + 8);
          doc.text(`Phone: ${merchant.phone}`, PDF_CONFIG.POSITIONS.INDENT_X, yPosition + 16);
          doc.text(`Status: ${merchant.status ? 'Active' : 'Inactive'}`, PDF_CONFIG.POSITIONS.INDENT_X, yPosition + 24);
          yPosition += PDF_CONFIG.SPACING.DETAIL_ITEM_SPACING;
        });
      }

      // Add detailed device data
      if (data.detailedData?.devices && data.detailedData.devices.length > 0) {
        doc.addPage();
        yPosition = PDF_CONFIG.POSITIONS.NEW_PAGE_START_Y;

        doc.setFontSize(PDF_CONFIG.FONT_SIZES.SUBTITLE);
        doc.setFont('helvetica', 'bold');
        doc.text('Devices Details', PDF_CONFIG.POSITIONS.START_X, yPosition);
        yPosition += PDF_CONFIG.SPACING.SECTION_SPACING;

        doc.setFontSize(PDF_CONFIG.FONT_SIZES.SMALL);
        doc.setFont('helvetica', 'normal');

        data.detailedData.devices.forEach((device: any, index: number) => {
          if (yPosition > PDF_CONFIG.POSITIONS.PAGE_BREAK_THRESHOLD) {
            doc.addPage();
            yPosition = PDF_CONFIG.POSITIONS.NEW_PAGE_START_Y;
          }

          doc.setFont('helvetica', 'bold');
          doc.text(`${index + 1}. ${device.name}`, PDF_CONFIG.POSITIONS.START_X, yPosition);
          doc.setFont('helvetica', 'normal');
          doc.text(`ID: ${device.identifier}`, PDF_CONFIG.POSITIONS.INDENT_X, yPosition + 8);
          doc.text(`Type: ${device.type}`, PDF_CONFIG.POSITIONS.INDENT_X, yPosition + 16);
          doc.text(`Status: ${device.status ? 'Active' : 'Inactive'}`, PDF_CONFIG.POSITIONS.INDENT_X, yPosition + 24);
          yPosition += PDF_CONFIG.SPACING.DETAIL_ITEM_SPACING;
        });
      }

      // Add recent transactions if available
      if (data.detailedData?.transactions && data.detailedData.transactions.length > 0) {
        doc.addPage();
        yPosition = PDF_CONFIG.POSITIONS.NEW_PAGE_START_Y;

        doc.setFontSize(PDF_CONFIG.FONT_SIZES.SUBTITLE);
        doc.setFont('helvetica', 'bold');
        doc.text('Recent Transactions', PDF_CONFIG.POSITIONS.START_X, yPosition);
        yPosition += PDF_CONFIG.SPACING.SECTION_SPACING;

        doc.setFontSize(PDF_CONFIG.FONT_SIZES.SMALL);
        doc.setFont('helvetica', 'normal');

        data.detailedData.transactions.slice(0, PDF_CONFIG.LIMITS.MAX_TRANSACTIONS).forEach((transaction: any, index: number) => {
          if (yPosition > PDF_CONFIG.POSITIONS.PAGE_BREAK_THRESHOLD) {
            doc.addPage();
            yPosition = PDF_CONFIG.POSITIONS.NEW_PAGE_START_Y;
          }

          const transactionText =
            transaction.description || transaction.id || `Transaction ${index + 1}`;
          doc.text(`${index + 1}. ${transactionText}`, PDF_CONFIG.POSITIONS.START_X, yPosition);
          yPosition += PDF_CONFIG.SPACING.TRANSACTION_SPACING;
        });
      }
    }

    // Add footer to all pages
    const pageCount = (doc as any).internal.getNumberOfPages();
    for (let i = 1; i <= pageCount; i++) {
      doc.setPage(i);
      doc.setFontSize(PDF_CONFIG.FONT_SIZES.FOOTER);
      doc.setFont('helvetica', 'italic');
      doc.setTextColor(
        PDF_CONFIG.COLORS.FOOTER_TEXT[0],
        PDF_CONFIG.COLORS.FOOTER_TEXT[1],
        PDF_CONFIG.COLORS.FOOTER_TEXT[2]
      );
      doc.text(
        PDF_CONFIG.FOOTER.TEXT,
        PDF_CONFIG.POSITIONS.START_X,
        doc.internal.pageSize.height - PDF_CONFIG.POSITIONS.FOOTER_Y_OFFSET
      );
      doc.text(
        PDF_CONFIG.FOOTER.CONFIDENTIAL,
        PDF_CONFIG.POSITIONS.START_X,
        doc.internal.pageSize.height - PDF_CONFIG.POSITIONS.FOOTER_TEXT_Y_OFFSET
      );
    }

    return doc;
  } catch (error: any) {
    console.error('Error in generatePDF:', error);
    throw new Error(`PDF generation failed: ${error.message}`);
  }
};
